<template>
    <Dialog
        v-model:visible="state.visible"
        :style="{ width: '450px' }"
        :header="state.headerText"
        :modal="true"
        class="p-fluid dialog-header"
        @hide="$emit('hide-dialog', true)"
    >
        <div v-if="state.loading">
            <ScaleLoader />
        </div>
        <template v-else>
            <!-- Creates a user first in the db with the general information-->
            <div class="field">
                <label for="block-name">First Name</label>
                <InputText maxlength = "255" :class="{ 'p-invalid': state.submitted && !state.user.name }" v-model="state.user.name" placeholder="Enter your first name" />
                <small v-if="state.submitted && !state.user.name" class="p-invalid">First Name is required.</small>
            </div>
            <div class="field">
                <label for="block-name">Last Name</label>
                <InputText maxlength = "255" :class="{ 'p-invalid': state.submitted && !state.user.surname }" v-model="state.user.surname" placeholder="Enter your last name" />
                <small v-if="state.submitted && !state.user.surname" class="p-invalid">Last Name is required.</small>
            </div>
            <div class="field">
                <label for="block-name">Email</label>
                <InputText maxlength = "255" :class="{ 'p-invalid': state.submitted && !state.user.email }" v-model="state.user.email" placeholder="Enter your user email" />
                <small v-if="state.submitted && !state.user.email" class="p-invalid">Email is required.</small>
            </div>

            <Toolbar>
                <template v-slot:start v-if="state.readMfa">
                    <label for="mfa" class="mr-2">Enable MFA</label>
                    <InputSwitch v-model="state.user.mail_otp" :disabled="!state.writeMfa" />
                </template>
                <template v-slot:end>
                    <Button label="Cancel" icon="pi pi-times" class="p-button-danger" @click="$emit('hide-dialog', true)" />
                    <Button label="Submit" icon="pi pi-check" class="ml-2 p-button-primary" @click="submit" />
                </template>
            </Toolbar>
        </template>
    </Dialog>
</template>

<script>
import { reactive, computed, onMounted, defineComponent } from 'vue';
import { UserPermissions } from '@/enums';
import useUserComposable from '../../composables/UserComposable';

import { fieldInputValidator, emailValidator } from '@/helpers/form-validators';
import useValidate from '@vuelidate/core';
import GlobalRequest from '@/api/global-request';

import store from '@/store';
import { useRouter } from 'vue-router';

const company_id = computed(() => {
    return store.getters['auth/userCompanyId']?.id;
});

export default defineComponent({
    emits: ['hide-dialog'],

    props: {
        editId: {
            default: null,
            required: false
        }
    },
    watch: {
        editId: {
            handler: function (userId) {
                if (userId === null) {
                    this.state.user = {
                        name: null,
                        surname: null,
                        email: null,
                        mail_otp: 0,
                        frozen: 0,
                        service_number: null,
                    };
                    this.state.headerText = 'Add New User'
                } else {
                    this.state.headerText = 'Edit User'
                    this.state.loading = true

                    GlobalRequest.getRequest(`/v2/admin/users/${userId}`)
                        .then((response) => {
                            if (Object.hasOwn(response, 'data')) {
                                this.state.user.name = response.data.name
                                this.state.user.surname = response.data.surname
                                this.state.user.email = response.data.email
                                this.state.user.mail_otp = response.data.mail_otp === 1
                                this.state.user.frozen = response.data.frozen
                                this.state.user.service_number = response.data.service_number

                                this.state.loading = false

                                console.log('loading response.data.mail_otp', response.data)
                            }
                        });
                }
            },
            immediate: true
        }
    },

    setup() {
        const { checkUserPermission, addUser, getAllUsers } = useUserComposable();
        // when mounted, check the user permission for mfa read and write first
        onMounted(() => {
            state.readMfa = checkUserPermission(UserPermissions.READ_MFA);
            state.writeMfa = checkUserPermission(UserPermissions.WRITE_MFA);
        });

        const createInitialState = () => {
            return reactive({
                visible: true,
                readMfa: false,
                writeMfa: false,
                submitted: false,
                loading: false,
                headerText: 'Add New User',
                user: {
                    name: null,
                    surname: null,
                    email: null,
                    mail_otp: 0,
                    frozen: 0,
                    service_number: null,
                },
            });
        };
        const state = createInitialState();

        const router = useRouter()

        const rules = computed(() => {
            return {
                user: {
                    name: fieldInputValidator('First Name'),
                    surname: fieldInputValidator('Last Name'),
                    email: emailValidator(),
                },
            };
        });
        const v$ = useValidate(rules, state);

        return { v$, state, addUser, getAllUsers, router };
    },
    methods: {
        submit() {
            if (this.userIsValid()) {

                if (this.$props.editId !== null) {
                    this.updateUser()
                } else {
                    this.createUser();
                }
            }
        },

        userIsValid() {
            this.v$.$validate();
            if (!this.v$.$error) {
                return true;
            }
            // Sends all the error messages by using the Toast
            this.v$.$errors.forEach((err) => {
                this.$toast.add({ severity: 'error', summary: 'Input Errors', detail: err.$message, life: 5000 });
            });
        },

        updateUser() {
            GlobalRequest.patchRequest(`/v2/admin/users/${this.$props.editId}`, {
                name: this.state.user.name,
                surname: this.state.user.surname,
                email: this.state.user.email,
                mail_otp: this.state.user.mail_otp,
                frozen: this.state.user.frozen,
                service_number: this.state.user.service_number,
                company_id: company_id.value,
            })
                .then(() => {
                    this.state.visible = false
                    this.getAllUsers()
                })
        },

        async createUser() {
            // console.log(this.state.user);
            const newUser = await this.addUser(this.state.user);
            if (newUser) {
                this.$emit('hide-dialog', true);
                // await this.getAllUsers();

                await this.router.push({
                    name: 'AnswerAssessment',
                    params: { userId: parseInt(newUser.id), assessmentCode: 'user_profile' }
                });

                //  https://localhost:8080/form/user_profile/705
            }
        },
    },
});
</script>

<style scoped>
.p-toolbar {
    background: none;
    border: none;
}
</style>
